import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  incidentAdminService,
  SecurityIncidentRow,
  SecurityIncidentStatus,
} from '../services/incidentAdminService';

// FEAT-077 — Admin incident response workflow (MVP-18.45).

const STATUS_COLOR: Record<SecurityIncidentStatus, 'default' | 'warning' | 'error' | 'success'> = {
  DRAFT: 'default',
  CONFIRMED: 'warning',
  NOTIFICATION_SENT: 'success',
  CLOSED: 'default',
};

export default function IncidentResponsePage() {
  const [incidents, setIncidents] = useState<SecurityIncidentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [operatorEmail, setOperatorEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selected, setSelected] = useState<SecurityIncidentRow | null>(null);

  const loadIncidents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await incidentAdminService.listIncidents();
      setIncidents(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load incidents');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadIncidents();
  }, [loadIncidents]);

  const handleCreate = async () => {
    if (!title.trim() || !description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await incidentAdminService.createIncident({ title: title.trim(), description: description.trim() });
      setCreateOpen(false);
      setTitle('');
      setDescription('');
      await loadIncidents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (incident: SecurityIncidentRow) => {
    setSubmitting(true);
    setError(null);
    try {
      await incidentAdminService.confirmIncident(incident.id);
      await loadIncidents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to confirm incident');
    } finally {
      setSubmitting(false);
    }
  };

  const handleNotify = async (incident: SecurityIncidentRow) => {
    if (!operatorEmail.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await incidentAdminService.notifyIncident(incident.id, operatorEmail.trim());
      await loadIncidents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to trigger notification');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }} data-testid="incident-response-page">
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Incident Response
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, confirm, and trigger breach notification workflows (MVP-18.45).
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={() => void loadIncidents()} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" onClick={() => setCreateOpen(true)} data-testid="create-incident-btn">
            New Incident
          </Button>
        </Stack>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Title</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {incidents.map((incident) => (
                <TableRow
                  key={incident.id}
                  hover
                  onClick={() => setSelected(incident)}
                  sx={{ cursor: 'pointer' }}
                  data-testid={`incident-row-${incident.id}`}
                >
                  <TableCell>{incident.title}</TableCell>
                  <TableCell>
                    <Chip label={incident.status} color={STATUS_COLOR[incident.status]} size="small" />
                  </TableCell>
                  <TableCell>{new Date(incident.createdAt).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    {incident.status === 'DRAFT' ? (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          void handleConfirm(incident);
                        }}
                        disabled={submitting}
                        data-testid={`confirm-incident-${incident.id}`}
                      >
                        Confirm
                      </Button>
                    ) : null}
                    {incident.status === 'CONFIRMED' ? (
                      <Button
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelected(incident);
                        }}
                        data-testid={`notify-incident-${incident.id}`}
                      >
                        Notify
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
              {incidents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No incidents recorded yet.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Security Incident</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            margin="normal"
            inputProps={{ 'data-testid': 'incident-title-input' }}
          />
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            margin="normal"
            multiline
            minRows={3}
            inputProps={{ 'data-testid': 'incident-description-input' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleCreate()} disabled={submitting}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} maxWidth="md" fullWidth>
        <DialogTitle>{selected?.title}</DialogTitle>
        <DialogContent>
          {selected ? (
            <Stack spacing={2}>
              <Typography variant="body2">{selected.description}</Typography>
              <Chip label={selected.status} color={STATUS_COLOR[selected.status]} sx={{ alignSelf: 'flex-start' }} />
              {selected.status === 'CONFIRMED' ? (
                <TextField
                  fullWidth
                  label="Operator email"
                  type="email"
                  value={operatorEmail}
                  onChange={(e) => setOperatorEmail(e.target.value)}
                  inputProps={{ 'data-testid': 'operator-email-input' }}
                />
              ) : null}
              {selected.notificationLog?.length ? (
                <Box data-testid="notification-log">
                  <Typography variant="subtitle2" gutterBottom>
                    Notification log
                  </Typography>
                  {selected.notificationLog.map((entry, index) => (
                    <Typography key={index} variant="caption" display="block">
                      {String(entry.channel)} → {String(entry.recipient)} ({String(entry.status)})
                    </Typography>
                  ))}
                </Box>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Close</Button>
          {selected?.status === 'CONFIRMED' ? (
            <Button
              color="error"
              variant="contained"
              onClick={() => selected && void handleNotify(selected)}
              disabled={submitting || !operatorEmail.trim()}
              data-testid="trigger-notification-btn"
            >
              Trigger Notification
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>
    </Container>
  );
}
